import { Injectable } from "@angular/core";
import { ContextExtractor } from "projects/borrower-app/src/app/services/context-extractor";
import { IDecideNextStep } from "projects/borrower-app/src/app/services/wizard/next-step-decider/next-step-decider.interface";
import { WizardFlowContext } from "../../../wizard-flow-context.model";
import { CreateAccountStep } from "../../create-account-step.model";
import { ContextType } from "../context-type.enum";
import { CreateAccountStepConfig } from "../create-account-step-config.model";
import { FieldConfigUtils } from "../field-config-utils";
import { StepType } from "../step-type.enum";
import { NextStepDeciderFactory } from "./next-step-decider.factory";
import { StepFactoryBase } from "./step-factory-base";
import { ICreateStep } from "./step-factory.interface";

@Injectable()
export class CreateAccountStepFactory extends StepFactoryBase<CreateAccountStepConfig> implements ICreateStep {

  constructor(private readonly _nextStepDeciderFactory: NextStepDeciderFactory) {
      super();
  }
  configure(step: CreateAccountStep, config: CreateAccountStepConfig): void {
    config.explanation = step.explanation;
    config.title = step.title;
    config.heading = step.heading;
    config.groupId = step.groupId;
    config._i18n = step._i18n;
    config.fieldConfig = step.fieldConfig;
  }

  create(config: CreateAccountStepConfig, wizardFlowContext: WizardFlowContext): CreateAccountStep {
    let nextStepDecider: IDecideNextStep | undefined = undefined;
    if (config.nextStepDeciderConfig) {
        nextStepDecider = this._nextStepDeciderFactory.create(config.nextStepDeciderConfig, wizardFlowContext);
    }
    const mortgage = ContextExtractor.extractContext(wizardFlowContext, ContextType.Mortgage);
    const borrower = mortgage.borrowers[0];
    const createAccountStep = new CreateAccountStep(config.id, config.name, config.path, borrower, nextStepDecider);
    createAccountStep.title = config.title;
    createAccountStep.explanation = config.explanation;
    createAccountStep.heading = config.heading;
    // Alias step/config to one _i18n bag the way GenericStepFactory does, so translations written
    // onto the config (e.g. smsConsentText) survive configure()'s config._i18n = step._i18n.
    if (!config._i18n) {
      config._i18n = {};
    }
    createAccountStep._i18n = config._i18n;

    // LLS-569: this step sits in the registration flow, which WizardFlowConfigService does not
    // default-merge on load (only applicationFlowConfig steps get that), so merge here the way
    // GenericStepFactory does: add newly registered keys, drop keys no longer generated, and
    // alias step/config to one object so field-config-editor edits survive configure().
    const defaultFieldConfig = FieldConfigUtils.generateDefaultFieldConfigForStep(StepType.CreateAccount);
    const fieldConfig = config.fieldConfig && Object.keys(config.fieldConfig).length ? { ...config.fieldConfig } : {};
    for (const key of Object.keys(defaultFieldConfig)) {
      if (!(key in fieldConfig)) {
        fieldConfig[key] = defaultFieldConfig[key];
      }
    }
    for (const key of Object.keys(fieldConfig)) {
      if (!(key in defaultFieldConfig)) {
        delete fieldConfig[key];
      }
    }
    config.fieldConfig = fieldConfig;
    createAccountStep.fieldConfig = fieldConfig;
    return createAccountStep;
  }
}
